<template>
  <div class="contact-directory-search-bar" :class="{ 'has-error': !isInputValid }">
    <BaseIcon name="search" class="contact-directory-search-bar__icon" />
    <input
      :placeholder="$t('page.contact-directory.search-bar-placeholder')"
      :value="value"
      enterkeyhint="search"
      @keydown.enter="onSearchKey"
      @input="input"
      class="contact-directory-search-bar__input"
      ref="searchBarInput"
      @blur="performSearch"
    />
    <BaseIcon
      v-if="value"
      name="x"
      class="contact-directory-search-bar__clear"
      @mousedown.prevent
      @click="clearSearch"
      v-tooltip="{ content: $t('actions.erase'), placement: 'top', offset: '8px', delay: 400 }"
    />
    <BaseShortcut
      value="~"
      isActionable
      @shortcut-clicked="addTilde"
      v-tooltip="{ content: $t('search.tild'), placement: 'top', delay: 500 }"
    >
    </BaseShortcut>
    <BaseButton
      isSquare
      :isTransparent="!exactMatch"
      isSmall
      @click.native="toggleExactMatch"
      v-tooltip="{ content: $t('search.exact-match'), placement: 'top', delay: 500 }"
    >
      <BaseIcon name="format-letter-matches" class="contact-directory-search-bar__input-icon" />
    </BaseButton>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseShortcut from '@/components/base/BaseShortcut';
import BaseIcon from '@/components/base/BaseIcon.vue';
import DebounceMixin from '@/mixins/debounce-mixin';

import { mapActions, mapGetters } from 'vuex';
import { closeKeyboardOnTouch } from '@/util/search-key';

export default {
  name: 'ContactDirectorySearchBar',
  mixins: [DebounceMixin],
  components: {
    BaseIcon,
    BaseButton,
    BaseShortcut,
  },
  data() {
    return {
      value: '',
      valid: true,
    };
  },
  computed: {
    ...mapGetters('contactDirectorySearchFilters', ['searchFilters', 'exactMatch']),
    isInputValid() {
      return this.valid;
    },
  },
  methods: {
    ...mapActions('contactDirectorySearchFilters', ['setKeywords', 'setExactMatch']),
    input(event) {
      this.value = event.target.value;
      this.debounce(() => {
        this.performSearch(event);
      }, 2000);
    },
    // On a touch screen, the keyboard closes and the field's blur searches.
    onSearchKey(event) {
      if (!closeKeyboardOnTouch(event.target)) this.performSearch(event);
    },
    async performSearch(event) {
      // It replaces the search the typing had planned, which would run it again.
      this.debounce(null, 0, true);
      if (this.isInputValid && this.value.length > 0) {
        const query = event.target.value;
        await this.setKeywords({ keywords: query });
        this.$emit('search');
      }
    },
    addTilde() {
      this.value = this.value + '~';
      this.$nextTick(() => {
        this.$refs.searchBarInput.focus();
      });
    },
    async clearSearch() {
      this.value = '';
      await this.setKeywords({ keywords: '' });
      this.$emit('search');
      this.$nextTick(() => {
        if (this.$refs.searchBarInput) this.$refs.searchBarInput.focus();
      });
    },
    toggleExactMatch() {
      this.setExactMatch(!this.exactMatch);
      if (this.exactMatch) {
        this.value = `"${this.value}"`;
        if (this.value === '""') {
          this.$nextTick(() => {
            this.$refs.searchBarInput.focus();
            this.$refs.searchBarInput.setSelectionRange(1, 1);
          });
        }
      } else {
        this.value = this.value.replace(/"/g, '');
      }
    },
    checkInputValidity() {
      const regex = /"[^"]*"~/;
      if (regex.test(this.value) || this.checkUnmatchedQuotes(this.value)) {
        this.valid = false;
      } else {
        this.valid = true;
      }
    },
    getQuoteCount(input) {
      return (input.match(/"/g) || []).length;
    },
    checkUnmatchedQuotes(input) {
      const quoteCount = this.getQuoteCount(input);
      if (quoteCount > 0 && quoteCount % 2 !== 0) {
        return true;
      }
      return false;
    },
    checkExactMatchStatus() {
      const quoteCount = this.getQuoteCount(this.value);
      if (quoteCount <= 0) return this.setExactMatch(false);

      if (quoteCount % 2 === 0) {
        this.setExactMatch(true);
      }
    },
  },
  watch: {
    value() {
      this.checkInputValidity();
      this.checkExactMatchStatus();
    },
  },
  mounted() {
    this.value = this.searchFilters.keywords || '';
    this.$nextTick(() => {
      if (this.$refs.searchBarInput) {
        this.$refs.searchBarInput.focus();
      }
    });
  },
};
</script>

<style lang="scss">
.contact-directory-search-bar {
  position: relative;
  width: calc(100% - 6px);
  background: $color-slate-50;
  border-radius: $border-radius-root;
  border: 1px solid $color-border;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 16px;
  gap: 8px;
  min-height: 52px;
  margin: 2px;
  transition: border 200ms ease, box-shadow 200ms ease;

  &:focus-within {
    border-color: $color-accent;
    box-shadow: 0 0 0 3px rgba($color-accent-500, 0.1);

    .contact-directory-search-bar__icon {
      color: $color-accent;
    }
  }

  &.has-error {
    border-color: $color-error;
  }

  &__input {
    display: flex;
    background: transparent;
    outline: none;
    flex-grow: 1 !important;
    align-items: center;
    border: 1px solid transparent;
    font-size: 16px;

    &::placeholder {
      color: $color-text-muted;
      font-style: normal;
    }
  }

  &__input-icon {
    width: 20px;
    height: 20px;
  }

  &__clear {
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    cursor: pointer;
    color: $color-normal;
  }

  &__icon {
    flex: 0 1 auto;
    width: 20px;
    display: flex;
    color: $color-normal-pale;
  }
}
</style>
